import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Download, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPaymentStatus, guideDownloadUrl } from "@/lib/jobbkartaApi";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const st = await getPaymentStatus(sessionId);
        if (!active) return;
        if (st.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (st.payment_status === "expired" || st.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (e) {
        /* keep retrying */
      }
      tries.current += 1;
      if (tries.current > 30) {
        if (active) setState("error");
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-5 py-10">
      <Link to="/" className="mb-8 font-display text-xl font-extrabold tracking-tight text-slate-900">
        Spontan<span className="text-emerald-600">.</span>
      </Link>
      <div
        data-testid="payment-success-card"
        className="w-[min(480px,92vw)] rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">
              Bekräftar din betalning…
            </h1>
            <p className="mt-2 text-sm text-slate-500">Ett ögonblick – vi låser upp din guide.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h1 className="mt-4 font-display text-2xl font-extrabold text-slate-900">Tack för ditt köp!</h1>
            <p className="mt-2 text-sm text-slate-600">
              Din guide är klar att ladda ned. Länken fungerar en begränsad tid efter köpet, så
              spara gärna filen direkt.
            </p>
            <a href={guideDownloadUrl(sessionId)} data-testid="download-guide-btn">
              <Button className="mt-6 h-12 w-full gap-2 rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                <Download className="h-5 w-5" /> Ladda ned PDF
              </Button>
            </a>
            <Link
              to="/"
              className="mt-4 inline-block text-sm text-slate-500 hover:text-slate-700"
            >
              Tillbaka till Spontan.
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-4 font-display text-xl font-extrabold text-slate-900">
              Vi kunde inte bekräfta betalningen
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Om pengar har dragits, vänta en liten stund och ladda om sidan. Annars kan du prova
              att köpa igen.
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-6 rounded-full">
                Tillbaka till start
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
